import { useContext, useState } from 'react';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import { useField } from 'formik';
import type { FieldHookConfig } from 'formik';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import InputGroup from 'react-bootstrap/InputGroup';

import { useComponentDidUpdate } from '../../hooks';
import { FormContext, isRequiredField } from './utils';
import SimpleMaskInput from './SimpleMaskInput';
import type { MaskChangeEvent, SimpleMaskInputProps } from './SimpleMaskInput';

const StyledMaskedInput = styled(SimpleMaskInput)`
  order: 1;

  &:placeholder-shown:not(:focus) + * {
    left: 50px !important;
  }
`;

const StyledInputPrepend = styled(InputGroup.Prepend)`
  opacity: 1 !important;
  order: 0;
`;

const checkPhoneIsFederal = (phoneNumber: string): boolean => {
  if (phoneNumber && phoneNumber.length >0 && ((phoneNumber[0] === '9') || (phoneNumber[0] === '8'))) { return true; };
  return false;
}

/**
 * Пропсы Formik-обёртки телефонного ввода (hooks-стиль, как MaskInput).
 *
 * `maskPattern`/`numericOnly`/`formatDisplayValue` фиксированы внутри и Omit'ятся;
 * `required`/`onComplete` переобъявлены с типом MaskChangeEvent.
 *
 * `error` из базы НЕ Omit'ится: PhoneInput читает `props?.error ?? error`
 * (внутренний useState — фолбэк), и реальный потребитель (ClientLookup.jsx)
 * передаёт `error={ error }`.
 *
 * `dontCheckPhoneIsFederal` приходит из FormContext, НЕ из props.
 */
export interface PhoneInputProps extends Omit<SimpleMaskInputProps,
  'required' | 'onComplete' | 'maskPattern' | 'numericOnly' | 'formatDisplayValue'> {
  /** Ручной признак обязательности; иначе автодетект по validationSchema (isRequiredField). */
  required?: boolean;
  onComplete?: (event: MaskChangeEvent) => void;
  /** Колбэк вызывается, когда поле переходит в состояние ошибки валидации. */
  onValidateFail?: (error: unknown) => void;
  children?: React.ReactNode;
}

const PhoneInput = (props: PhoneInputProps) => {
  const [error, setError] = useState(undefined);
  const {
    validationSchema, setFieldValue, setFieldTouched, submitCount, dontCheckPhoneIsFederal = false,
  } = useContext(FormContext) as {
    validationSchema?: any;
    setFieldValue: any;
    setFieldTouched: any;
    submitCount: number;
    dontCheckPhoneIsFederal?: boolean;
  };
  const {
    required, onComplete, onValidateFail, ...rest
  } = props;

  // Приведение: FieldHookConfig конфликтует с наследуемыми HTML-подобными пропсами
  // (напр., maxLength: string | number) — миграция типов, runtime не меняется.
  const [field, meta] = useField({
    validate: (number = '') => {
      if ([null, undefined, ''].indexOf(number) !== -1) {
        return null;
      }

      // eslint-disable-next-line no-useless-escape
      const bPhone = number.replace(/[^\d\+]/g, '');
      const phone = parsePhoneNumberFromString(bPhone.includes('+7') ? bPhone : `+7${ bPhone }`);

      if (!phone || !phone.isValid() || (!checkPhoneIsFederal(phone?.nationalNumber) && dontCheckPhoneIsFederal)) {
        return 'Ошибка ввода телефонного номера';
      }

      return null;
    },
    ...props,
  } as FieldHookConfig<any>);

  // так как апи возвращает номер телефона с кодом страны,
  // а в маске ввода этот код нам не нужен ибо на данный момент, соответствуя задаче AV-2708,
  // код страны всегда отображается в виде bootstrap input prepend компонента, то удалим его
  // @see https://avtoapp.atlassian.net/browse/AV-2708
  const formatDisplayValue = (displayValue: string) => displayValue?.replace(/\+7\s?/g, '');

  useComponentDidUpdate(() => {
    if (!isEmpty(meta.error)
    && ((meta.touched && (field.value !== undefined)) || (submitCount > 0))
    ) {
      setError(meta.error);

      if (typeof onValidateFail === 'function') {
        onValidateFail(meta.error);
      }
    } else {
      setError(undefined);
    }
  }, [meta.error, meta.touched, submitCount, field.value]);

  const handleChangeCompleted = (event: MaskChangeEvent) => {
    let typedNumber;
    const { rawValue: unmaskedValue } = event.target;

    if (unmaskedValue) {
      const asYouType = new AsYouType('RU');
      asYouType.input(unmaskedValue);
      const typed = asYouType.getNumber();
      typedNumber = ['80', '81', '82', '83', '84', '85', '86', '87', '87', '88'].includes(unmaskedValue.substring(0, 2)) ? `+78${ typed.nationalNumber }` : typed.number;
    }

    if (typedNumber !== field.value) {
      setFieldValue(props.name, typedNumber);
      setFieldTouched(props.name, true, true);
    }

    if (typeof onComplete === 'function') {
      onComplete(event);
    }
  };

  return (
    <StyledMaskedInput
      { ...rest }
      { ...field }
      maskPattern='(000) 000-00-00'
      error={ props?.error ?? error }
      required={ required || isRequiredField(validationSchema, field.name) }
      formatDisplayValue={ formatDisplayValue }
      onComplete={ handleChangeCompleted }
      numericOnly
    >
      <StyledInputPrepend>
        <InputGroup.Text>+7</InputGroup.Text>
      </StyledInputPrepend>

      { props.children }
    </StyledMaskedInput>
  );
};

export default PhoneInput;
